import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { FormGroup, FormBuilder, Validators, AbstractControl, FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { Component, OnInit } from '@angular/core';

import { IAccount, AuthService, IContact, GetCurrentContact } from '@ebb-v2/ebb-integration';
import { map } from 'rxjs/operators';
import { Apollo } from 'apollo-angular';
import { AuthenticationService } from '../../data-services';
import { DialogActivateCompleteComponent } from './dialog-activate-complete.component';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

class CrossFieldErrorMatcherPassword implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return (control.touched && control.errors) || (form.errors && form.errors.password);
  }
}

@Component({
  selector: 'ebb-portal-activate',
  templateUrl: './activate.component.html',
  styleUrls: ['./activate.component.scss']
})
export class ActivateComponent implements OnInit {
  public accountForm: FormGroup;
  public emailSendSuccess: boolean;
  public requestCounter = 0;
  public requesting: boolean;
  public username: string;
  public delayInterval: NodeJS.Timer;
  public passwordMatcherEmail = new CrossFieldErrorMatcherPassword();
  public emailSendError = false;
  public messageDisplay = '';
  public messageDisplayShowLogout = false;
  public tooManyAttempts: boolean;
  public badCode = 0;
  public contact$: Observable<any>;

  constructor(
    private apiAuthentication: AuthenticationService,
    private _fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private route: ActivatedRoute,
    private apollo: Apollo,
    public dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.apiAuthentication.getCurrentAuthentication().subscribe(user => {
      if(user.isAuthenticated === false || user.isVerified === true){
        this.router.navigate(['login']);
      } else {
        const query = GetCurrentContact;
        this.contact$ = this.apollo.query({
          query
        })
        .pipe(map(result => result.data));
        
        this.requestNewCode(null);
      }
    });

    this.accountForm = this._fb.group({
      displayName: { value: '', disabled: true },
      emailCode: [null, Validators.required],
      password: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(100),
        Validators.pattern(/([A-Z]){1,}/),
        Validators.pattern(/([a-z]){1,}/),
        Validators.pattern(/([0-9]){1,}/),
        Validators.pattern(/([~`!@#$%\^\&*\)\()]){1,}/)
      ]],
      confirmPassword: ['', Validators.required],
    }, { validators: this.confirmValidator });
    
  }

  public returnToLogin(): void {
    this.router.navigate(['/login']);
  }
  
  public requestNewCode(e): void {
    this.requesting = true;
    if (this.requestCounter === 2) {
      this.codeRequestDelay();
    }
    if (this.requestCounter < 3) {
      this.contact$.subscribe(( data ) => {
        const readContact = data.contact as IContact;
        this.username = readContact.emailAddress1;
        this.accountForm.get('displayName').setValue(this.username);
        this.authService.sendVerifyEmail().subscribe(res => {
          if (this.emailSendSuccess) {
            this.requestCounter++;
          }
          if (res.status && !this.emailSendSuccess) {
            this.emailSendSuccess = res.status;
          }
          this.emailSendError = false;
          this.requesting = false;
        }, () => {
          console.error('error sending email');
          this.emailSendError = true;
          this.requesting = false;
        });
      });
    }
    if(e) {e.preventDefault();}
  }

  public logout(e): void {
    const redirectEndpoint = (this.router.url.split('?')[0]).replace('//','/' );
    this.authService.logout(redirectEndpoint).subscribe(url => {
      document.location.href = url;
    });
    e.preventDefault();
  }

  private codeRequestDelay(): void {
    if (!this.delayInterval) {
      this.delayInterval = setTimeout(() => {
        this.requestCounter = 0;
        this.requesting = false;
        this.delayInterval = null;
      }, 180000);
    }
  }

  public submitAccount(): void {
    const accountInfo = this.accountForm.getRawValue();
    delete accountInfo['confirmPassword'];

    this.setMessage('');
    this.authService.createAccountFromGuest(accountInfo).subscribe(res => {
      if (res.message && res.message !== 'success') {
        if (res.message === 'bad email code' && this.badCode < 3) {
          this.badCode++;
          this.setMessage(res.message);
        } else if (res.message === 'bad email code' && this.badCode > 2) {
          this.setMessage('There have been too many requests to verify this email address. Please wait a while, then try again.');
          this.tooManyAttempts = true;
          setTimeout(() => {
            this.badCode = 0;
            this.tooManyAttempts = false;
            this.setMessage('');
          }, 300000);
        } else {
          this.setMessage(res.message);
        }
      } else {
        //success
        const dialogRef = this.dialog.open(DialogActivateCompleteComponent, {
          width: '250px',
          disableClose: true
        });
    
        dialogRef.afterClosed().subscribe(result => {
          const redirectEndpoint = (this.router.url.split('?')[0]).replace('//','/' );
          this.authService.logout(redirectEndpoint).subscribe(url => {
            document.location.href = url;
          });
        });
      }
    });
  }

  private confirmValidator(c: AbstractControl): { password: boolean } {
    if (c.get('confirmPassword').value && c.get('password').value !== c.get('confirmPassword').value) {
      return { password: true }
    }
  }
  
  private setMessage(message: string) {
    this.messageDisplay = message;
    this.messageDisplayShowLogout = false;
  
    if(message === 'success' || message === ''){
      this.messageDisplay = '';
      this.messageDisplayShowLogout = true;
      return;
    }

    if(message === 'bad email code'){
      this.messageDisplay = 'Invalid Confirmation Email Code Entered';
      return;
    }
    if(message === 'Another object with the same value for property userPrincipalName already exists.'){
      this.messageDisplay = 'That email address is already registered with the program';
      this.messageDisplayShowLogout = true;
      return;
    }
  }
}
